import { useRef } from "react"
import { useResizeObserver } from "usehooks-ts"
import { cn } from "@/lib/utils"

interface AutoScaleProps {
  deviceWidth: number;
  deviceHeight: number;
  children: React.ReactNode;
  contentClassName?: string;
  frameClassName?: string;
  scaleBy?: "width" | "height";
}

interface AutoScaleFrameProps extends AutoScaleProps {
  availableWidth?: number;
  availableHeight?: number;
}

export function AutoScaleFrame({
  availableWidth,
  availableHeight,
  deviceWidth,
  deviceHeight,
  contentClassName,
  frameClassName,
  scaleBy = "width",
  children,
}: AutoScaleFrameProps) {
  if (!availableWidth || !availableHeight) return null

  let scale =
    scaleBy === "width"
      ? availableWidth / deviceWidth
      : availableHeight / deviceHeight
  let previewWidth = Math.floor(scale * deviceWidth)
  let previewHeight = Math.floor(scale * deviceHeight)

  if (scaleBy === "width" && previewHeight > availableHeight) {
    previewHeight = availableHeight
    previewWidth = Math.floor((deviceWidth * availableHeight) / deviceHeight)
    scale = availableHeight / deviceHeight
  }

  return (
    <div
      className={cn("overflow-hidden relative h-full", frameClassName)}
      style={{
        width: `${previewWidth}px`,
        height: `${previewHeight}px`,
      }}
    >
      <div
        className={cn(
          "origin-top-left pointer-events-none h-full",
          contentClassName,
        )}
        style={{
          transform: `scale(${scale})`,
          width: deviceWidth,
          height: deviceHeight,
        }}
      >
        {children}
      </div>
    </div>
  )
}

export default function AutoScale({
  deviceWidth,
  deviceHeight,
  contentClassName,
  frameClassName,
  scaleBy = "width",
  children,
}: AutoScaleProps) {
  const previewRef = useRef<HTMLDivElement>(null)
  const { width, height } = useResizeObserver({ ref: previewRef })

  return (
    <div
      ref={previewRef}
      className="flex flex-grow min-h-0 items-start justify-center w-full"
    >
      <AutoScaleFrame
        availableWidth={width}
        availableHeight={height}
        deviceWidth={deviceWidth}
        deviceHeight={deviceHeight}
        contentClassName={contentClassName}
        frameClassName={frameClassName}
        scaleBy={scaleBy}
      >
        {children}
      </AutoScaleFrame>
    </div>
  )
}
